import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  Microsoft360View,
  MicrosoftTeamsAudioConferencing,
  SelectFieldConfig,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormConfig } from '../../../../forms/ziro-forms-models';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { Observable, Subscription, throwError } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MicrosoftTeamsAudioConferencingResource } from '../../../shared/resources/microsoft-teams-audio-conferencing.resource';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap } from 'rxjs/operators';
import { Microsoft360ViewContext } from '../../../../shared/contexts/microsoft-360-view.context';
import { cloneDeep } from 'lodash';

interface MicrosoftTeamsAudioConferencingFormData {
  audioConferenceNumber: ZiroSelectOption;
}

@Component({
  selector: 'ziro-ms-teams-audio-conferencing-form',
  templateUrl: './teams-audio-conferencing-form.component.html',
  styleUrls: ['./teams-audio-conferencing-form.component.scss'],
  standalone: false,
})
export class MsTeamsAudioConferencingFormComponent
  extends ZiroFormAbstractDirective<MicrosoftTeamsAudioConferencing, MicrosoftTeamsAudioConferencingFormData>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private bottomNavService = inject(BottomNavService);
  private msTeamsAudioConferencingResource = inject(MicrosoftTeamsAudioConferencingResource);
  private microsoft360ViewContext = inject(Microsoft360ViewContext);
  private translateService = inject(TranslateService);
  private router = inject(Router);

  readonly fieldConfig = input<SelectFieldConfig<string>>();
  readonly upn = input<string>();
  readonly tenantId = input<number>();

  ziroIcons = ZiroIcons;
  formConfig: ZiroFormConfig;

  private _microsoft360View: Microsoft360View;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.microsoft360ViewContext.state$.subscribe((state: Microsoft360View) => {
      this._microsoft360View = state;
    });

    this._initForm();
    this._initBottomNav();

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscriptions.add(formSubmittedSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  private _initForm(): void {
    const formattedPossibleOptions: ZiroSelectOption[] = this.fieldConfig().possibleOptions.map((option) => {
      return {
        value: option,
        label: option,
      };
    });

    this.formConfig = {
      fields: {
        audioConferenceNumber: {
          label: 'tkey;shared.model.ms_teams_audio_conferencing.form.audio_conference_number.label',
          componentConfig: new ZiroSelectConfig({ options: formattedPossibleOptions }),
          dataAutomation: 'ms-teams-audio-conferencing-number',
          required: () => this.fieldConfig().required,
          defaultValue: () => this.fieldConfig().defaultValue,
        },
      },
    } as ZiroFormConfig;
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ms-teams-audio-conferencing-cancel',
          dataAutomation: 'ms-teams-audio-conferencing-cancel',
          label: 'tkey;global.button.cancel.text',
          buttonClass: ButtonStyles.DEFAULT,
          cb: () => {
            this.router.navigate(['../'], { relativeTo: this.route });
          },
        },
        {
          id: 'ms-teams-audio-conferencing-save',
          dataAutomation: 'ms-teams-audio-conferencing-save',
          label: 'tkey;global.button.save.text',
          icon: this.ziroIcons.SAVE_SUCCESS,
          buttonClass: ButtonStyles.PRIMARY,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  protected submit(): Observable<void> {
    return this._onSaveClicked();
  }

  private _onSaveClicked(): Observable<void> {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );
    this._setPending(true);

    return this.msTeamsAudioConferencingResource.put(this.tenantId(), this.upn(), this.entity).pipe(
      tap(() => {
        this._ziroFormStateService.setIsFormDirty(false);
        const ms360 = cloneDeep(this._microsoft360View);
        ms360.teamsAudioConferencing = this.entity;
        this.microsoft360ViewContext._stateSource.next(ms360);
        this.microsoft360ViewContext.refresh(this.upn()).subscribe();

        this.toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.TEAMS_CALLING,
          'tkey;shared.toast.save.success.title',
          this.translateService.instant('tkey;shared.toast.save.success.message', {
            type: this.translateService.instant('tkey;shared.model.ms_teams_audio_conferencing.text'),
            name: this.upn(),
          }),
        );
        this.router.navigate(['../'], { relativeTo: this.route });
      }),
      catchError((response) => {
        this._setPending(false);
        if (response.status === 422) {
          this.toastService.push(
            ToastTypes.ERROR,
            `${this.ziroIcons.TEAMS_CALLING} text-danger`,
            'tkey;shared.toast.save.fail.title',
            response.error.description,
          );
        } else {
          return throwError(() => response);
        }
      }),
    );
  }

  private _setPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'ms-teams-audio-conferencing-save',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  toEntity = (formData: MicrosoftTeamsAudioConferencingFormData): MicrosoftTeamsAudioConferencing => {
    return {
      ...this.entity,
      audioConferenceNumber: formData.audioConferenceNumber?.value,
    };
  };

  toFormData = (entity: MicrosoftTeamsAudioConferencing): MicrosoftTeamsAudioConferencingFormData => {
    return {
      audioConferenceNumber: {
        value: entity.audioConferenceNumber,
        label: entity.audioConferenceNumber,
      },
    };
  };
}
